import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  approveAccessRequest,
  assignAdminUserToMe,
  cancelApprovalRequest,
  createAdminUser,
  deleteAdminUser,
  declineAccessRequest,
  fetchAccessRequests,
  fetchApprovalEmailStatus,
  fetchApprovalRequests,
  fetchLicensePackages,
  fetchAdminUsers,
  fetchOnlineUsers,
  resendApprovalRequest,
  setAdminUserAssignment,
  unassignAdminUser,
  updateAdminUser,
} from '../api/auth';
import GoToDashboardButton from './GoToDashboardButton';
import PasswordStrengthMeter, { getPasswordStrength } from './PasswordStrengthMeter';
import styles from './AdminPanel.module.css';

const emptyForm = {
  username: '',
  password: '',
  email: '',
  fullName: '',
  company: '',
  department: '',
  plant: '',
  team: '',
  project: '',
  requestType: 'pilot',
  licensePackage: 'evaluation',
  adminNote: '',
  role: 'normal',
  isActive: true,
  requireEmailApproval: true,
  requireLicenseAgreement: false,
};

export default function AdminPanel({ currentUser }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isSuperadmin = currentUser?.role === 'admin' && currentUser?.isSuperadmin === true;
  const [users, setUsers] = useState([]);
  const [online, setOnline] = useState({ count: 0, users: [], windowMs: 120000 });
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [selectedOnlineUser, setSelectedOnlineUser] = useState(null);
  const [accessRequests, setAccessRequests] = useState([]);
  const [approvalRequests, setApprovalRequests] = useState([]);
  const [licensePackages, setLicensePackages] = useState([]);
  const [requestReviewDrafts, setRequestReviewDrafts] = useState({});
  const [assignmentDrafts, setAssignmentDrafts] = useState({});
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [userFilters, setUserFilters] = useState({
    search: '',
    role: '',
    company: '',
    status: '',
    license: '',
    assignment: '',
  });
  const [createMailStatus, setCreateMailStatus] = useState(null);
  const [approvingAccessRequestId, setApprovingAccessRequestId] = useState(null);
  const [decliningAccessRequestId, setDecliningAccessRequestId] = useState(null);
  const [resendingApprovalId, setResendingApprovalId] = useState(null);
  const [cancellingApprovalId, setCancellingApprovalId] = useState(null);
  const [selectedApprovalRequest, setSelectedApprovalRequest] = useState(null);
  const [selectedRequestBucket, setSelectedRequestBucket] = useState(null);
  const [approvalEmailStatus, setApprovalEmailStatus] = useState({
    configured: true,
    providers: [],
    missing: [],
  });
  const approvalEmailConfigured = Boolean(approvalEmailStatus?.configured);

  const admins = useMemo(
    () => users.filter((user) => user.role === 'admin' && user.isActive),
    [users]
  );
  const delegatedAdmins = useMemo(
    () => admins.filter((user) => Number(user.id) !== Number(currentUser?.id)),
    [admins, currentUser?.id]
  );
  const normalUsers = useMemo(
    () => users.filter((user) => user.role === 'normal'),
    [users]
  );
  const managedByCurrentAdminCount = useMemo(
    () => normalUsers.filter((user) => Number(user.assignedAdminUserId) === Number(currentUser?.id)).length,
    [currentUser?.id, normalUsers]
  );
  const unassignedUserCount = useMemo(
    () => normalUsers.filter((user) => !user.assignedAdminUserId).length,
    [normalUsers]
  );
  const activeUsers = useMemo(
    () => users.filter((user) => user.isActive).length,
    [users]
  );
  const pendingExtensionUsers = useMemo(
    () => users.filter((user) => Number(user.pendingExtensionRequestsCount) > 0),
    [users]
  );
  const pendingExtensionRequestCount = useMemo(
    () => pendingExtensionUsers.reduce((sum, user) => sum + Number(user.pendingExtensionRequestsCount || 0), 0),
    [pendingExtensionUsers]
  );
  const createPasswordValid = useMemo(
    () => getPasswordStrength(form.password).isValid,
    [form.password]
  );
  const accessRequestBuckets = useMemo(() => ({
    approved: accessRequests.filter((request) => request.status === 'approved'),
    pending: accessRequests.filter((request) => request.status === 'pending'),
    declined: accessRequests.filter((request) => request.status !== 'pending' && request.status !== 'approved'),
  }), [accessRequests]);
  const approvalRequestBuckets = useMemo(() => ({
    approved: approvalRequests.filter((request) => request.status === 'approved'),
    pending: approvalRequests.filter((request) => request.status === 'pending'),
    declined: approvalRequests.filter((request) => request.status !== 'pending' && request.status !== 'approved'),
  }), [approvalRequests]);
  const filteredUsers = useMemo(() => {
    const search = userFilters.search.trim().toLowerCase();
    return users.filter((user) => {
      if (userFilters.role && user.role !== userFilters.role) return false;
      if (userFilters.company && String(user.company || '').trim().toLowerCase() !== userFilters.company.trim().toLowerCase()) return false;
      if (userFilters.status === 'active' && !user.isActive) return false;
      if (userFilters.status === 'inactive' && user.isActive) return false;
      if (userFilters.status === 'pending' && user.approvalStatus !== 'pending') return false;
      if (userFilters.license === 'required' && !user.requiresLicenseAcceptance) return false;
      if (userFilters.license === 'not_required' && user.requiresLicenseAcceptance) return false;
      if (userFilters.assignment === 'assigned' && !user.assignedAdminUserId) return false;
      if (userFilters.assignment === 'unassigned' && user.assignedAdminUserId) return false;
      if (userFilters.assignment === 'mine' && Number(user.assignedAdminUserId) !== Number(currentUser?.id)) return false;
      if (!search) return true;
      return [
        user.username,
        user.email,
        user.fullName,
        user.company,
        user.department,
        user.plant,
        user.team,
        user.project,
      ].some((value) => String(value || '').toLowerCase().includes(search));
    });
  }, [currentUser?.id, userFilters, users]);
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAdminUsers();
      const nextUsers = Array.isArray(data.users) ? data.users : [];
      setUsers(nextUsers);
      setEditing(Object.fromEntries(nextUsers.map((user) => [user.id, toEditState(user)])));
      setAssignmentDrafts(Object.fromEntries(
        nextUsers
          .filter((user) => user.role === 'normal')
          .map((user) => [user.id, user.assignedAdminUserId ? String(user.assignedAdminUserId) : ''])
      ));
    } catch (err) {
      setError(err.message || t('Unable to load users.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadOnlineUsers = useCallback(async () => {
    try {
      const data = await fetchOnlineUsers();
      setOnline({
        count: Number(data.count) || 0,
        users: Array.isArray(data.users) ? data.users : [],
        windowMs: Number(data.windowMs) || 120000,
      });
    } catch (err) {
      console.warn(t('Unable to load online users:'), err);
    }
  }, [t]);

  const loadApprovalRequests = useCallback(async () => {
    if (!isSuperadmin) {
      setApprovalRequests([]);
      return;
    }
    try {
      const data = await fetchApprovalRequests(12);
      setApprovalRequests(Array.isArray(data.requests) ? data.requests : []);
    } catch (err) {
      console.warn('Unable to load approval requests:', err);
    }
  }, [isSuperadmin]);

  const loadAccessRequests = useCallback(async () => {
    if (!isSuperadmin) {
      setAccessRequests([]);
      return;
    }
    try {
      const data = await fetchAccessRequests(20);
      const nextRequests = Array.isArray(data.requests) ? data.requests : [];
      setAccessRequests(nextRequests);
      setRequestReviewDrafts((current) => {
        const next = { ...current };
        for (const request of nextRequests) {
          next[request.id] = next[request.id] || {
            requireLicenseAgreement: Boolean(request.requireLicenseAgreement),
            declineReason: '',
            internalNote: request.internalNote || '',
            licensePackage: request.licensePackage || 'evaluation',
          };
        }
        return next;
      });
    } catch (err) {
      console.warn('Unable to load access requests:', err);
    }
  }, [isSuperadmin]);

  const loadApprovalEmailStatus = useCallback(async () => {
    try {
      const data = await fetchApprovalEmailStatus();
      setApprovalEmailStatus({
        configured: Boolean(data?.configured),
        providers: Array.isArray(data?.providers) ? data.providers : [],
        missing: Array.isArray(data?.missing) ? data.missing : [],
      });
    } catch (err) {
      console.warn('Unable to load approval email status:', err);
      setApprovalEmailStatus({
        configured: false,
        providers: [],
        missing: ['mail_provider'],
      });
    }
  }, []);

  const loadLicensePackages = useCallback(async () => {
    try {
      const data = await fetchLicensePackages();
      setLicensePackages(Array.isArray(data.packages) ? data.packages : []);
    } catch (err) {
      console.warn('Unable to load license packages:', err);
    }
  }, []);

  useEffect(() => {
    if (currentUser?.role !== 'admin') {
      navigate('/dashboard', { replace: true });
      return;
    }
    loadUsers();
    loadOnlineUsers();
    if (isSuperadmin) {
      loadAccessRequests();
      loadApprovalRequests();
    }
    loadApprovalEmailStatus();
    loadLicensePackages();
    const intervalId = window.setInterval(loadOnlineUsers, 15000);
    return () => window.clearInterval(intervalId);
  }, [currentUser?.role, isSuperadmin, loadAccessRequests, loadApprovalEmailStatus, loadApprovalRequests, loadLicensePackages, loadOnlineUsers, loadUsers, navigate]);

  useEffect(() => {
    if (!approvalEmailConfigured && form.requireEmailApproval) {
      setForm((prev) => ({ ...prev, requireEmailApproval: false, requireLicenseAgreement: false }));
    }
  }, [approvalEmailConfigured, form.requireEmailApproval]);

  useEffect(() => {
    if ((form.role === 'admin' || !form.email || !form.requireEmailApproval) && form.requireLicenseAgreement) {
      setForm((prev) => ({ ...prev, requireLicenseAgreement: false }));
    }
  }, [form.email, form.requireEmailApproval, form.requireLicenseAgreement, form.role]);

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateEdit = (id, key, value) => {
    setEditing((prev) => ({
      ...prev,
      [id]: { ...prev[id], [key]: value },
    }));
  };

  const updateRequestReviewDraft = (id, key, value) => {
    setRequestReviewDrafts((prev) => ({
      ...prev,
      [id]: {
        requireLicenseAgreement: false,
        declineReason: '',
        internalNote: '',
        licensePackage: 'evaluation',
        ...prev[id],
        [key]: value,
      },
    }));
  };

  const updateAssignmentDraft = (id, value) => {
    setAssignmentDrafts((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const toggleSelectedUser = (userId) => {
    setSelectedUserIds((current) => (
      current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId]
    ));
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    setCreateMailStatus(null);
    try {
      const result = await createAdminUser(form);
      setForm(emptyForm);
      setMessage(t(result.message || (result.approvalRequired ? 'User created and approval email sent.' : 'User created successfully.')));
      setCreateMailStatus(result.approvalEmail || null);
      await loadUsers();
      await loadAccessRequests();
      await loadApprovalRequests();
    } catch (err) {
      if (err.payload?.approvalEmail) {
        setCreateMailStatus(err.payload.approvalEmail);
      }
      if (err.payload?.user) {
        await loadUsers();
        await loadAccessRequests();
        await loadApprovalRequests();
      }
      setError(err.message || t('Unable to create user.'));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (user) => {
    const draft = editing[user.id];
    if (!draft) return;

    setSaving(true);
    setMessage('');
    setError('');
    try {
      await updateAdminUser(user.id, draft);
      setMessage(t('Saved {{username}}.', { username: draft.username }));
      await loadUsers();
      await loadAccessRequests();
      await loadApprovalRequests();
    } catch (err) {
      setError(err.message || t('Unable to update user.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    const confirmed = window.confirm(
      t('Delete user "{{username}}"? This cannot be undone.', { username: user.username })
    );
    if (!confirmed) return;

    setSaving(true);
    setMessage('');
    setError('');
    try {
      await deleteAdminUser(user.id);
      setMessage(t('Deleted {{username}}.', { username: user.username }));
      await loadUsers();
      await loadAccessRequests();
      await loadApprovalRequests();
    } catch (err) {
      setError(err.message || t('Unable to delete user.'));
    } finally {
      setSaving(false);
    }
  };

  const handleAssignToMe = async (user) => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const result = await assignAdminUserToMe(user.id);
      setMessage(t(result.message || 'User assigned successfully.'));
      await loadUsers();
    } catch (err) {
      setError(err.message || t('Unable to assign this user.'));
    } finally {
      setSaving(false);
    }
  };

  const handleUnassign = async (user) => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const result = await unassignAdminUser(user.id);
      setMessage(t(result.message || 'User assignment removed.'));
      await loadUsers();
    } catch (err) {
      setError(err.message || t('Unable to remove this assignment.'));
    } finally {
      setSaving(false);
    }
  };

  const handleSetAssignment = async (user) => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const value = assignmentDrafts[user.id] || '';
      const result = await setAdminUserAssignment(user.id, value ? Number(value) : null);
      setMessage(t(result.message || 'User assignment updated.'));
      await loadUsers();
    } catch (err) {
      setError(err.message || t('Unable to update this assignment.'));
    } finally {
      setSaving(false);
    }
  };

  const handleBulkUserStateChange = async (isActive) => {
    const candidates = users.filter((user) => selectedUserIds.includes(user.id) && user.id !== currentUser.id);
    if (!candidates.length) return;
    setSaving(true);
    setMessage('');
    setError('');
    try {
      for (const user of candidates) {
        await updateAdminUser(user.id, {
          ...(editing[user.id] || toEditState(user)),
          isActive,
        });
      }
      setSelectedUserIds([]);
      setMessage(t(isActive ? 'Selected users enabled.' : 'Selected users disabled.'));
      await loadUsers();
    } catch (err) {
      setError(err.message || t('Unable to update selected users.'));
    } finally {
      setSaving(false);
    }
  };

  const handleApproveAccessRequest = async (request) => {
    const draft = requestReviewDrafts[request.id] || {};
    setApprovingAccessRequestId(request.id);
    setMessage('');
    setError('');
    try {
      const result = await approveAccessRequest(request.id, {
        requireLicenseAgreement: Boolean(draft.requireLicenseAgreement),
        internalNote: draft.internalNote || '',
        licensePackage: draft.licensePackage || 'evaluation',
      });
      setMessage(t(result.message || 'Access request approved and approval email sent.'));
      await loadUsers();
      await loadAccessRequests();
      await loadApprovalRequests();
    } catch (err) {
      setError(err.message || t('Unable to approve access request.'));
      await loadAccessRequests();
    } finally {
      setApprovingAccessRequestId(null);
    }
  };

  const handleDeclineAccessRequest = async (request) => {
    const draft = requestReviewDrafts[request.id] || {};
    const confirmed = window.confirm(
      t('Decline access request for "{{username}}"?', { username: request.requestedUsername })
    );
    if (!confirmed) return;

    setDecliningAccessRequestId(request.id);
    setMessage('');
    setError('');
    try {
      const result = await declineAccessRequest(request.id, {
        declineReason: draft.declineReason || '',
        internalNote: draft.internalNote || '',
      });
      setMessage(t(result.message || 'Access request declined.'));
      await loadAccessRequests();
    } catch (err) {
      setError(err.message || t('Unable to decline access request.'));
      await loadAccessRequests();
    } finally {
      setDecliningAccessRequestId(null);
    }
  };

  const handleResendApproval = async (request) => {
    setResendingApprovalId(request.id);
    setMessage('');
    setError('');
    try {
      const result = await resendApprovalRequest(request.id);
      setResendingApprovalId(null);
      setMessage(t(result.message || 'Approval email sent successfully.'));
      await loadApprovalRequests();
    } catch (err) {
      setResendingApprovalId(null);
      setError(err.message || t('Approval email could not be sent.'));
      await loadApprovalRequests();
    }
  };

  const handleCancelApproval = async (request) => {
    const confirmed = window.confirm(
      t('Cancel the pending invitation for "{{username}}"?', { username: request.username })
    );
    if (!confirmed) return;

    setCancellingApprovalId(request.id);
    setMessage('');
    setError('');
    try {
      const result = await cancelApprovalRequest(request.id);
      setMessage(t(result.message || 'Invitation cancelled successfully.'));
      await loadApprovalRequests();
    } catch (err) {
      setError(err.message || t('Unable to cancel invitation.'));
      await loadApprovalRequests();
    } finally {
      setCancellingApprovalId(null);
    }
  };

  if (currentUser?.role !== 'admin') {
    return null;
  }

  return (
    <main className={styles.container}>
      <GoToDashboardButton />

      <header className={styles.header}>
        <div>
          <p className={styles.eyebrow}>{t('Administration')}</p>
          <h1 className={styles.title}>{t('Admin Panel')}</h1>
          <p className={styles.subtitle}>
            {t('Manage users, roles, passwords, and account access from one secure place.')}
          </p>
        </div>
        <div className={styles.currentAdmin}>
          <span>{t('Signed in as')}</span>
          <strong>{currentUser.username}</strong>
          <span>{isSuperadmin ? t('Superadmin') : t('Delegated admin')}</span>
          <button type="button" className={styles.secondaryButton} onClick={() => navigate('/admin/team')}>
            {t('Open admin team')}
          </button>
          {isSuperadmin ? (
            <button type="button" className={styles.secondaryButton} onClick={() => navigate('/admin/license-report')}>
              {t('Open license report')}
            </button>
          ) : null}
        </div>
      </header>

      <section className={styles.metricsGrid}>
        <Metric label={t('Total users')} value={users.length} />
        <Metric label={t('Active users')} value={activeUsers} />
        <Metric label={t('Online now')} value={online.count} />
        <Metric label={t('Active admins')} value={admins.length} />
        <Metric label={isSuperadmin ? t('Delegated admins') : t('Managed by me')} value={isSuperadmin ? delegatedAdmins.length : managedByCurrentAdminCount} />
        <Metric label={isSuperadmin ? t('Unassigned users') : t('Available to claim')} value={unassignedUserCount} />
        <Metric label={t('Pending extensions')} value={pendingExtensionRequestCount} />
      </section>

      <section className={styles.flowGrid}>
        <article className={styles.flowCard}>
          <span>{isSuperadmin ? t('Superadmin') : t('Delegated admin')}</span>
          <strong>{isSuperadmin ? t('Owns the whole company workspace') : t('Owns a working group')}</strong>
          <p>
            {isSuperadmin
              ? t('You control account creation, invitations, admin roles, assignments, licenses, and final account lifecycle decisions.')
              : t('You manage the users assigned to you, claim unassigned users, and keep your group moving without touching global governance.')}
          </p>
        </article>
        <article className={styles.flowCard}>
          <span>{t('Requests')}</span>
          <strong>{isSuperadmin ? t('You are the gatekeeper') : t('You cannot approve new accounts')}</strong>
          <p>
            {isSuperadmin
              ? t('All self-service access requests and invitation flows stay with you so account creation remains centralized.')
              : t('New account requests, invitation resends, and decline decisions stay with the superadmin.')}
          </p>
        </article>
        <article className={styles.flowCard}>
          <span>{t('Assignments')}</span>
          <strong>{isSuperadmin ? t('Route users to the right admin') : t('Claim and release normal users')}</strong>
          <p>
            {isSuperadmin
              ? t('Assign any normal user to a delegated admin, reassign them between admins, or return them to the unassigned pool.')
              : t('You can claim unassigned normal users for your group and release users currently assigned to you.')}
          </p>
        </article>
      </section>

      {(message || error) && (
        <div className={`${styles.notice} ${error ? styles.error : styles.success}`}>
          {error || message}
        </div>
      )}

      {pendingExtensionRequestCount > 0 ? (
        <div className={`${styles.notice} ${styles.warning}`}>
          {t('{{count}} account extension request(s) are waiting for admin review across {{users}} user(s). Open the user detail page to review each request, photo, profile, and plan state.', {
            count: pendingExtensionRequestCount,
            users: pendingExtensionUsers.length,
          })}
        </div>
      ) : null}

      {isSuperadmin ? (
        <>
          <section className={styles.panel}>
            <div className={styles.panelHeader}>
              <div>
                <h2>{t('Online Users')}</h2>
                <p>
                  {t('Users are counted online when their browser checks in within the last {{seconds}} seconds.', {
                    seconds: Math.round(online.windowMs / 1000),
                  })}
                </p>
              </div>
              <button className={styles.secondaryButton} onClick={loadOnlineUsers}>
                {t('Refresh online')}
              </button>
            </div>

            {online.users.length === 0 ? (
              <div className={styles.emptyState}>{t('No active users detected right now.')}</div>
            ) : (
              <div className={styles.onlineGrid}>
                {online.users.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    className={styles.onlineCardButton}
                    onClick={() => setSelectedOnlineUser(user)}
                  >
                    <div className={styles.onlineCard}>
                      <span className={styles.onlineDot} />
                      <div className={styles.onlineDetails}>
                        <strong>{user.username}</strong>
                        <p>
                          {t(roleLabel(user.role))} · {t('Last seen')} {formatDate(user.lastSeenAt, i18n.language)}
                        </p>
                        <span className={styles.onlineDetailLink}>{t('View details')}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>

          <section className={`${styles.panel} ${styles.activityPanel}`}>
            <div className={styles.panelHeader}>
              <div>
                <h2>{t('Access Activity')}</h2>
                <p>{t('Open the dedicated access-activity page to review every session with filters for username, date range, role, status, IP, environment, and visited pages.')}</p>
              </div>
              <button className={styles.primaryButton} onClick={() => navigate('/admin/activity')}>
                {t('Open access activity')}
              </button>
            </div>
            <div className={styles.emptyState}>{t('The full access log now lives in its own page so the admin panel stays compact and easier to manage.')}</div>
          </section>
        </>
      ) : null}

      {isSuperadmin ? (
      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2>{t('Create User')}</h2>
            <p>{t('Add a normal user or another administrator.')}</p>
          </div>
        </div>

        {!approvalEmailConfigured ? (
          <div className={`${styles.notice} ${styles.warning}`}>
            {buildApprovalEmailStatusMessage(t, approvalEmailStatus)}
          </div>
        ) : null}

        <form className={styles.createGrid} onSubmit={handleCreate}>
          <Field label={t('Username')}>
            <input
              value={form.username}
              onChange={(event) => updateForm('username', event.target.value)}
              required
              placeholder={t('username')}
            />
          </Field>
          <Field label={t('Password')}>
            <input
              type="password"
              value={form.password}
              onChange={(event) => updateForm('password', event.target.value)}
              required
              placeholder={t('Use 10+ characters with upper, lower, number, and symbol')}
            />
            <PasswordStrengthMeter password={form.password} compact />
          </Field>
          <Field label={t('Email')}>
            <input
              type="email"
              value={form.email}
              onChange={(event) => updateForm('email', event.target.value)}
              placeholder="name@example.com"
            />
          </Field>
          <Field label={t('Full name')}>
            <input value={form.fullName} onChange={(event) => updateForm('fullName', event.target.value)} />
          </Field>
          <Field label={t('Company')}>
            <input value={form.company} onChange={(event) => updateForm('company', event.target.value)} />
          </Field>
          <Field label={t('Department')}>
            <input value={form.department} onChange={(event) => updateForm('department', event.target.value)} />
          </Field>
          <Field label={t('Plant')}>
            <input value={form.plant} onChange={(event) => updateForm('plant', event.target.value)} />
          </Field>
          <Field label={t('Team')}>
            <input value={form.team} onChange={(event) => updateForm('team', event.target.value)} />
          </Field>
          <Field label={t('Project')}>
            <input value={form.project} onChange={(event) => updateForm('project', event.target.value)} />
          </Field>
          <Field label={t('Request type')}>
            <select value={form.requestType} onChange={(event) => updateForm('requestType', event.target.value)}>
              <option value="demo">{t('Demo')}</option>
              <option value="pilot">{t('Pilot')}</option>
              <option value="production">{t('Production use')}</option>
            </select>
          </Field>
          <Field label={t('License package')}>
            <select value={form.licensePackage} onChange={(event) => updateForm('licensePackage', event.target.value)}>
              {(licensePackages.length ? licensePackages : [
                { key: 'evaluation', label: 'Evaluation' },
                { key: 'pilot', label: 'Pilot' },
                { key: 'production', label: 'Production Candidate' },
              ]).map((item) => (
                <option key={item.key} value={item.key}>{item.label}</option>
              ))}
            </select>
          </Field>
          <Field label={t('Role')}>
            <select value={form.role} onChange={(event) => updateForm('role', event.target.value)}>
              <option value="normal">{t('Normal user')}</option>
              <option value="admin">{t('Admin')}</option>
            </select>
          </Field>
          <Field label={t('Internal admin note')}>
            <textarea value={form.adminNote} onChange={(event) => updateForm('adminNote', event.target.value)} rows={3} />
          </Field>
          <label className={styles.toggleField}>
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(event) => updateForm('isActive', event.target.checked)}
            />
            {t('Active account')}
          </label>
          <label className={styles.toggleField}>
            <input
              type="checkbox"
              checked={form.requireEmailApproval}
              onChange={(event) => updateForm('requireEmailApproval', event.target.checked)}
              disabled={form.role === 'admin' || !form.email || !approvalEmailConfigured}
            />
            {approvalEmailConfigured ? t('Require email approval') : t('Require email approval (unavailable)')}
          </label>
          <label className={styles.toggleField}>
            <input
              type="checkbox"
              checked={form.requireLicenseAgreement}
              onChange={(event) => updateForm('requireLicenseAgreement', event.target.checked)}
              disabled={form.role === 'admin' || !form.email || !form.requireEmailApproval || !approvalEmailConfigured}
            />
            {t('Require license agreement on password setup')}
          </label>
          <button className={styles.primaryButton} type="submit" disabled={saving || !createPasswordValid}>
            {saving ? t('Saving...') : t('Create user')}
          </button>
        </form>

        {createMailStatus ? (
          <div className={`${styles.notice} ${createMailStatus.sent ? styles.success : createMailStatus.skipped ? styles.info : styles.error}`}>
            {createMailStatus.sent ? t('Approval email sent successfully.') : createMailStatus.skipped ? t('Approval email was not needed for this user.') : t('Approval email could not be sent.')}
            {!createMailStatus.sent && createMailStatus.error ? ` ${t('Error')}: ${createMailStatus.error}` : ''}
          </div>
        ) : null}
      </section>
      ) : null}

      {isSuperadmin ? (
      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2>{t('Access Requests')}</h2>
            <p>{t('Review self-service normal-user requests before creating the actual account.')}</p>
          </div>
          <button className={styles.secondaryButton} onClick={loadAccessRequests}>
            {t('Refresh')}
          </button>
        </div>

        {accessRequests.length === 0 ? (
          <div className={styles.emptyState}>{t('No access requests recorded yet.')}</div>
        ) : (
          <div className={styles.bucketGrid}>
            {['approved', 'pending', 'declined'].map((status) => (
              <button
                key={status}
                type="button"
                className={`${styles.bucketCard} ${approvalStatusClass(styles, status)}`}
                onClick={() => setSelectedRequestBucket({ type: 'access', status })}
              >
                <span className={styles.bucketLabel}>{t(approvalStatusLabel(status))}</span>
                <strong className={styles.bucketValue}>{accessRequestBuckets[status].length}</strong>
                <span className={styles.bucketHint}>{t('View requests')}</span>
              </button>
            ))}
          </div>
        )}
      </section>
      ) : null}

      {isSuperadmin ? (
      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2>{t('Approval Requests')}</h2>
            <p>{t('Track pending, approved, expired, or failed email approvals for newly created users.')}</p>
          </div>
          <button className={styles.secondaryButton} onClick={loadApprovalRequests}>
            {t('Refresh')}
          </button>
        </div>

        {approvalRequests.length === 0 ? (
          <div className={styles.emptyState}>{t('No approval requests recorded yet.')}</div>
        ) : (
          <div className={styles.bucketGrid}>
            {['approved', 'pending', 'declined'].map((status) => (
              <button
                key={status}
                type="button"
                className={`${styles.bucketCard} ${approvalStatusClass(styles, status)}`}
                onClick={() => setSelectedRequestBucket({ type: 'approval', status })}
              >
                <span className={styles.bucketLabel}>{t(approvalStatusLabel(status))}</span>
                <strong className={styles.bucketValue}>{approvalRequestBuckets[status].length}</strong>
                <span className={styles.bucketHint}>{t('View requests')}</span>
              </button>
            ))}
          </div>
        )}
      </section>
      ) : null}

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2>{t('Existing Users')}</h2>
            <p>
              {isSuperadmin
                ? t('Edit username, reset passwords, change roles, disable accounts, or delete old users.')
                : t('Claim normal users into your working group, review their details, and manage the accounts assigned to you.')}
            </p>
          </div>
          <button className={styles.secondaryButton} onClick={loadUsers} disabled={loading || saving}>
            {t('Refresh')}
          </button>
        </div>

        {!isSuperadmin ? (
          <div className={`${styles.notice} ${styles.info}`}>
            {t('Only the superadmin can review account-creation requests, create admins, manage invitations, and control commercial licenses.')}
          </div>
        ) : null}

        <div className={styles.filterToolbar}>
          <input
            className={styles.filterInput}
            value={userFilters.search}
            onChange={(event) => setUserFilters((current) => ({ ...current, search: event.target.value }))}
            placeholder={t('Search username, email, company, department, plant, team, or project')}
          />
          <select className={styles.filterInput} value={userFilters.role} onChange={(event) => setUserFilters((current) => ({ ...current, role: event.target.value }))}>
            <option value="">{t('All roles')}</option>
            <option value="admin">{t('Admin')}</option>
            <option value="normal">{t('Normal user')}</option>
          </select>
          <select className={styles.filterInput} value={userFilters.status} onChange={(event) => setUserFilters((current) => ({ ...current, status: event.target.value }))}>
            <option value="">{t('All statuses')}</option>
            <option value="active">{t('Active')}</option>
            <option value="inactive">{t('Disabled')}</option>
            <option value="pending">{t('Pending approval')}</option>
          </select>
          <select className={styles.filterInput} value={userFilters.license} onChange={(event) => setUserFilters((current) => ({ ...current, license: event.target.value }))}>
            <option value="">{t('All license states')}</option>
            <option value="required">{t('License required')}</option>
            <option value="not_required">{t('License not required')}</option>
          </select>
          <select className={styles.filterInput} value={userFilters.assignment} onChange={(event) => setUserFilters((current) => ({ ...current, assignment: event.target.value }))}>
            <option value="">{t('All assignments')}</option>
            <option value="assigned">{t('Assigned')}</option>
            <option value="unassigned">{t('Unassigned')}</option>
            <option value="mine">{t('Assigned to me')}</option>
          </select>
        </div>

        {isSuperadmin && selectedUserIds.length ? (
          <div className={styles.bulkBar}>
            <strong>{t('{{count}} user(s) selected', { count: selectedUserIds.length })}</strong>
            <div className={styles.bulkActions}>
              <button type="button" className={styles.secondaryButton} onClick={() => handleBulkUserStateChange(true)} disabled={saving}>
                {t('Enable selected')}
              </button>
              <button type="button" className={styles.dangerButton} onClick={() => handleBulkUserStateChange(false)} disabled={saving}>
                {t('Disable selected')}
              </button>
            </div>
          </div>
        ) : null}

        {loading ? (
          <div className={styles.emptyState}>{t('Loading users...')}</div>
        ) : users.length === 0 ? (
          <div className={styles.emptyState}>{t('No users found.')}</div>
        ) : (
          <div className={styles.userList}>
            {filteredUsers.map((user) => {
              const draft = editing[user.id] || toEditState(user);
              const isSelf = currentUser.id === user.id;
              const assignedToCurrentAdmin = Number(user.assignedAdminUserId) === Number(currentUser.id);
              const assignedToAnotherAdmin = Number(user.assignedAdminUserId) > 0 && !assignedToCurrentAdmin;
              const canAssignToMe = !isSelf && user.role !== 'admin' && !assignedToCurrentAdmin && !assignedToAnotherAdmin;
              const canReleaseAssignment = !isSelf && user.role !== 'admin' && (isSuperadmin || assignedToCurrentAdmin);

              return (
                <article key={user.id} className={styles.userCard}>
                  <div className={styles.userTopline}>
                    <div>
                      {isSuperadmin ? (
                        <label className={styles.selectUserRow}>
                          <input
                            type="checkbox"
                            checked={selectedUserIds.includes(user.id)}
                            onChange={() => toggleSelectedUser(user.id)}
                          />
                          <span>{t('Select')}</span>
                        </label>
                      ) : null}
                      <span className={`${styles.roleBadge} ${user.role === 'admin' ? styles.adminBadge : ''}`}>
                        {t(roleLabel(user.role))}
                      </span>
                      {user.isSuperadmin ? (
                        <span className={`${styles.roleBadge} ${styles.adminBadge}`}>
                          {t('Superadmin')}
                        </span>
                      ) : null}
                      {user.approvalStatus && user.approvalStatus !== 'approved' ? (
                        <span className={`${styles.roleBadge} ${approvalStatusClass(styles, user.approvalStatus)}`}>
                          {t(approvalStatusLabel(user.approvalStatus))}
                        </span>
                      ) : null}
                      {user.assignedAdminUsername ? (
                        <span className={`${styles.roleBadge} ${styles.pendingBadge}`}>
                          {t('Assigned to {{admin}}', { admin: user.assignedAdminUsername })}
                        </span>
                      ) : null}
                      {Number(user.pendingExtensionRequestsCount) > 0 ? (
                        <span className={`${styles.roleBadge} ${styles.pendingBadge}`}>
                          {t('{{count}} extension request(s)', { count: Number(user.pendingExtensionRequestsCount) })}
                        </span>
                      ) : null}
                      <h3>{user.username}</h3>
                      <p>
                        {user.isActive ? t('Active') : t('Disabled')}
                        {isSelf ? ` · ${t('Current session')}` : ''}
                      </p>
                    </div>
                    <div className={styles.userId}>{t('ID')} #{user.id}</div>
                  </div>

                  <div className={styles.editGrid}>
                    <Field label={t('Username')}>
                      <input
                        value={draft.username}
                        onChange={(event) => updateEdit(user.id, 'username', event.target.value)}
                      />
                    </Field>
                    <Field label={t('Email')}>
                      <input
                        type="email"
                        value={draft.email}
                        onChange={(event) => updateEdit(user.id, 'email', event.target.value)}
                        placeholder="name@example.com"
                      />
                    </Field>
                    <Field label={t('New password')}>
                      <input
                        type="password"
                        value={draft.password}
                        onChange={(event) => updateEdit(user.id, 'password', event.target.value)}
                        placeholder={t('leave blank to keep')}
                      />
                      {draft.password ? <PasswordStrengthMeter password={draft.password} compact /> : null}
                    </Field>
                    <Field label={t('Role')}>
                      <select
                        value={draft.role}
                        onChange={(event) => updateEdit(user.id, 'role', event.target.value)}
                        disabled={!isSuperadmin}
                      >
                        <option value="normal">{t('Normal user')}</option>
                        <option value="admin">{t('Admin')}</option>
                      </select>
                    </Field>
                    <label className={styles.toggleField}>
                      <input
                        type="checkbox"
                        checked={draft.isActive}
                        onChange={(event) => updateEdit(user.id, 'isActive', event.target.checked)}
                      />
                      {t('Active account')}
                    </label>
                    <label className={styles.toggleField}>
                      <input
                        type="checkbox"
                        checked={Boolean(draft.requiresLicenseAcceptance)}
                        onChange={(event) => updateEdit(user.id, 'requiresLicenseAcceptance', event.target.checked)}
                        disabled={draft.role === 'admin' || !isSuperadmin}
                      />
                      {t('Requires license agreement')}
                    </label>
                  </div>

                  <div className={styles.userMeta}>
                    <span>{t('Full name')}: {user.fullName || '-'}</span>
                    <span>{t('Email')}: {user.email || '-'}</span>
                    <span>{t('Company')}: {user.company || '-'}</span>
                    <span>{t('Profile title')}: {user.profileTitle || '-'}</span>
                    <span>{t('Approval status')}: {t(approvalStatusLabel(user.approvalStatus || 'approved'))}</span>
                    <span>{t('Email verified')}: {formatDate(user.emailVerifiedAt, i18n.language)}</span>
                    <span>{t('License required')}: {user.requiresLicenseAcceptance ? t('Yes') : t('No')}</span>
                    <span>{t('Assigned admin')}: {user.assignedAdminUsername || '-'}</span>
                  </div>

                  {isSuperadmin && user.role === 'normal' ? (
                    <div className={styles.assignmentRow}>
                      <Field label={t('Assigned admin')}>
                        <select
                          value={assignmentDrafts[user.id] || ''}
                          onChange={(event) => updateAssignmentDraft(user.id, event.target.value)}
                        >
                          <option value="">{t('Unassigned pool')}</option>
                          {delegatedAdmins.map((admin) => (
                            <option key={admin.id} value={String(admin.id)}>
                              {admin.username}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <button
                        className={styles.secondaryButton}
                        onClick={() => handleSetAssignment(user)}
                        disabled={saving}
                      >
                        {t('Apply assignment')}
                      </button>
                    </div>
                  ) : null}

                  <div className={styles.userMeta}>
                    <span>{t('Created')}: {formatDate(user.createdAt, i18n.language)}</span>
                    <span>{t('Updated')}: {formatDate(user.updatedAt, i18n.language)}</span>
                  </div>

                  <div className={styles.cardActions}>
                    <button
                      className={styles.secondaryButton}
                      onClick={() => navigate(`/admin/users/${user.id}`)}
                    >
                      {t('Open detail')}
                    </button>
                    <button
                      className={styles.primaryButton}
                      onClick={() => handleSave(user)}
                      disabled={saving || Boolean(draft.password && !getPasswordStrength(draft.password).isValid)}
                    >
                      {t('Save changes')}
                    </button>
                    {canAssignToMe ? (
                      <button
                        className={styles.secondaryButton}
                        onClick={() => handleAssignToMe(user)}
                        disabled={saving}
                      >
                        {t('Assign to me')}
                      </button>
                    ) : null}
                    {canReleaseAssignment ? (
                      <button
                        className={styles.secondaryButton}
                        onClick={() => handleUnassign(user)}
                        disabled={saving}
                      >
                        {t('Release')}
                      </button>
                    ) : null}
                    {isSuperadmin ? (
                      <button
                        className={styles.dangerButton}
                        onClick={() => handleDelete(user)}
                        disabled={saving || isSelf}
                        title={isSelf ? t('You cannot delete your own active session account') : ''}
                      >
                        {t('Delete user')}
                      </button>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {selectedOnlineUser ? (
        <div className={styles.modalBackdrop} onClick={() => setSelectedOnlineUser(null)}>
          <div className={styles.modalCard} onClick={(event) => event.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <p className={styles.modalEyebrow}>{t('Online user')}</p>
                <h3>{selectedOnlineUser.username}</h3>
                <p>
                  {t(roleLabel(selectedOnlineUser.role))} · {t('Last seen')} {formatDate(selectedOnlineUser.lastSeenAt, i18n.language)}
                </p>
              </div>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => setSelectedOnlineUser(null)}
              >
                {t('Close')}
              </button>
            </div>

            <div className={styles.modalMetaGrid}>
              <Meta label="IP" value={selectedOnlineUser.meta?.ip} />
              <Meta label={t('Country')} value={selectedOnlineUser.meta?.country || t('Unknown')} />
              <Meta label={t('Browser')} value={selectedOnlineUser.meta?.browser} />
              <Meta label="OS" value={selectedOnlineUser.meta?.os} />
              <Meta label={t('Device')} value={selectedOnlineUser.meta?.device} />
              <Meta label={t('Timezone')} value={selectedOnlineUser.meta?.client?.timezone} />
              <Meta label={t('Language')} value={selectedOnlineUser.meta?.client?.language} />
              <Meta label={t('Screen')} value={selectedOnlineUser.meta?.client?.screen} />
              <Meta label={t('Viewport')} value={selectedOnlineUser.meta?.client?.viewport} />
              <Meta label={t('Page')} value={selectedOnlineUser.meta?.client?.path} />
            </div>
          </div>
        </div>
      ) : null}

      {selectedApprovalRequest ? (
        <div className={styles.modalBackdrop} onClick={() => setSelectedApprovalRequest(null)}>
          <div className={styles.modalCard} onClick={(event) => event.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <p className={styles.modalEyebrow}>{t('Approval request details')}</p>
                <h3>{selectedApprovalRequest.username}</h3>
                <p>{selectedApprovalRequest.email}</p>
              </div>
              <button
                className={styles.secondaryButton}
                type="button"
                onClick={() => setSelectedApprovalRequest(null)}
              >
                {t('Close')}
              </button>
            </div>

            <div className={styles.modalMetaGrid}>
              <div>
                <dt>{t('Approval status')}</dt>
                <dd>{t(approvalStatusLabel(selectedApprovalRequest.status))}</dd>
              </div>
              <div>
                <dt>{t('Email delivery')}</dt>
                <dd>
                  {t(deliveryStatusLabel(selectedApprovalRequest.deliveryStatus))}
                  {selectedApprovalRequest.deliveryProvider
                    ? ` (${t(deliveryProviderLabel(selectedApprovalRequest.deliveryProvider))})`
                    : ''}
                </dd>
              </div>
              <div>
                <dt>{t('Requested')}</dt>
                <dd>{formatDate(selectedApprovalRequest.requestedAt, i18n.language)}</dd>
              </div>
              <div>
                <dt>{t('Email sent')}</dt>
                <dd>{formatDate(selectedApprovalRequest.emailSentAt, i18n.language)}</dd>
              </div>
              <div>
                <dt>{t('Expires')}</dt>
                <dd>{formatDate(selectedApprovalRequest.expiresAt, i18n.language)}</dd>
              </div>
              <div>
                <dt>{t('Requested by')}</dt>
                <dd>{selectedApprovalRequest.requestedByUsername || '-'}</dd>
              </div>
              <div>
                <dt>{t('License required')}</dt>
                <dd>{selectedApprovalRequest.requiresLicenseAgreement ? t('Yes') : t('No')}</dd>
              </div>
              <div>
                <dt>{t('Cancelled')}</dt>
                <dd>{formatDate(selectedApprovalRequest.cancelledAt, i18n.language)}</dd>
              </div>
              <div>
                <dt>{t('Cancelled by')}</dt>
                <dd>{selectedApprovalRequest.cancelledByUsername || '-'}</dd>
              </div>
            </div>

            {selectedApprovalRequest.cancelledReason ? (
              <div className={styles.timelinePanel}>
                <strong>{t('Cancellation reason')}</strong>
                <pre className={styles.errorDetailsText}>{selectedApprovalRequest.cancelledReason}</pre>
              </div>
            ) : null}

            {selectedApprovalRequest.deliveryError ? (
              <div className={styles.errorDetailsPanel}>
                <strong>{t('Exact backend error')}</strong>
                <pre className={styles.errorDetailsText}>{selectedApprovalRequest.deliveryError || '-'}</pre>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {selectedRequestBucket ? (
        <div className={styles.modalBackdrop} onClick={() => setSelectedRequestBucket(null)}>
          <div className={styles.modalCard} onClick={(event) => event.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <p className={styles.modalEyebrow}>
                  {selectedRequestBucket.type === 'access' ? t('Access Requests') : t('Approval Requests')}
                </p>
                <h3>{t(approvalStatusLabel(selectedRequestBucket.status))}</h3>
                <p>
                  {selectedRequestBucket.type === 'access'
                    ? t('{{count}} access request(s)', { count: accessRequestBuckets[selectedRequestBucket.status].length })
                    : t('{{count}} approval request(s)', { count: approvalRequestBuckets[selectedRequestBucket.status].length })}
                </p>
              </div>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => setSelectedRequestBucket(null)}
              >
                {t('Close')}
              </button>
            </div>

            <div className={styles.modalBucketList}>
              {selectedRequestBucket.type === 'access'
                ? accessRequestBuckets[selectedRequestBucket.status].map((request) => renderAccessRequestCard({
                  request,
                  t,
                  i18n,
                  styles,
                  requestReviewDrafts,
                  updateRequestReviewDraft,
                  approvalEmailConfigured,
                  approvingAccessRequestId,
                  decliningAccessRequestId,
                  handleApproveAccessRequest,
                  handleDeclineAccessRequest,
                }))
                : approvalRequestBuckets[selectedRequestBucket.status].map((request) => renderApprovalRequestCard({
                  request,
                  t,
                  i18n,
                  styles,
                  resendingApprovalId,
                  cancellingApprovalId,
                  handleResendApproval,
                  handleCancelApproval,
                  setSelectedApprovalRequest,
                }))}
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}

function Metric({ label, value }) {
  return (
    <div className={styles.metricCard}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className={styles.field}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd title={value || ''}>{value || '-'}</dd>
    </div>
  );
}

function toEditState(user) {
  return {
    username: user.username || '',
    email: user.email || '',
    password: '',
    fullName: user.fullName || '',
    company: user.company || '',
    department: user.department || '',
    plant: user.plant || '',
    team: user.team || '',
    project: user.project || '',
    requestType: user.requestType || 'demo',
    licensePackage: user.licensePackage || 'evaluation',
    adminNote: user.adminNote || '',
    role: user.role === 'admin' ? 'admin' : 'normal',
    isActive: Boolean(user.isActive),
    requiresLicenseAcceptance: Boolean(user.requiresLicenseAcceptance),
    onboardingCompletedAt: user.onboardingCompletedAt || null,
  };
}

function approvalStatusLabel(status) {
  switch (status) {
    case 'pending':
      return 'Pending approval';
    case 'approved':
      return 'Approved';
    case 'declined':
      return 'Declined';
    case 'cancelled':
      return 'Cancelled';
    case 'expired':
      return 'Expired';
    case 'email_failed':
      return 'Email failed';
    case 'superseded':
      return 'Superseded';
    default:
      return status || 'Approved';
  }
}

function approvalStatusClass(stylesObject, status) {
  switch (status) {
    case 'pending':
      return stylesObject.pendingBadge;
    case 'approved':
      return stylesObject.approvedBadge;
    case 'declined':
      return stylesObject.expiredBadge;
    case 'expired':
      return stylesObject.expiredBadge;
    case 'cancelled':
      return stylesObject.expiredBadge;
    case 'email_failed':
      return stylesObject.emailFailedBadge;
    default:
      return '';
  }
}

function roleLabel(role) {
  return role === 'admin' ? 'Admin' : 'Normal user';
}

function deliveryStatusLabel(status) {
  switch (status) {
    case 'sent':
      return 'Sent';
    case 'failed':
      return 'Failed';
    case 'pending':
    default:
      return 'Pending';
  }
}

function deliveryProviderLabel(provider) {
  const normalized = String(provider || '').trim().toLowerCase();
  if (normalized === 'smtp') return 'SMTP';
  if (normalized === 'resend') return 'Resend';
  if (normalized === 'gmail_api') return 'Gmail API';
  return provider || 'Unknown';
}

function buildApprovalEmailStatusMessage(t, status = {}) {
  const missing = new Set(Array.isArray(status.missing) ? status.missing : []);
  const providers = Array.isArray(status.providers) ? status.providers : [];

  if (missing.has('mail_provider') && missing.has('approval_base_url')) {
    return t('Email approval is unavailable because no mail provider or public approval URL is configured.');
  }

  if (missing.has('mail_provider')) {
    return t('Email approval is unavailable because no mail provider is configured.');
  }

  if (missing.has('approval_base_url')) {
    const providerList = providers.map((provider) => t(deliveryProviderLabel(provider))).join(', ');
    return `${t('Email approval is unavailable because the public approval URL is not configured.')} ${t('Configured providers: {{providers}}.', { providers: providerList || '-' })} ${t('Set SMARTI_PUBLIC_APP_URL or APP_BASE_URL for approval links.')}`;
  }

  return t('Email approval is unavailable because no mail provider is configured.');
}

function formatDate(value, locale) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(locale || undefined);
}

function renderAccessRequestCard({
  request,
  t,
  i18n,
  styles,
  requestReviewDrafts,
  updateRequestReviewDraft,
  approvalEmailConfigured,
  approvingAccessRequestId,
  decliningAccessRequestId,
  handleApproveAccessRequest,
  handleDeclineAccessRequest,
}) {
  const draft = requestReviewDrafts[request.id] || {
    requireLicenseAgreement: false,
    declineReason: '',
  };
  const timelineItems = buildTimelineItems([
    { label: t('Requested'), value: request.createdAt },
    { label: t('Reviewed'), value: request.reviewedAt },
  ]);

  return (
    <article key={request.id} className={styles.userCard}>
      <div className={styles.userTopline}>
        <div>
          <span className={`${styles.roleBadge} ${approvalStatusClass(styles, request.status)}`}>
            {t(approvalStatusLabel(request.status))}
          </span>
          <h3>{request.requestedUsername}</h3>
          <p>{request.requestedEmail}</p>
        </div>
        <div className={styles.userId}>#{request.id}</div>
      </div>

      <div className={styles.userMeta}>
        <span>{t('Full name')}: {request.fullName || '-'}</span>
        <span>{t('Company')}: {request.company || '-'}</span>
        <span>{t('Request type')}: {request.requestType || '-'}</span>
        <span>{t('License package')}: {request.licensePackage || '-'}</span>
        <span>{t('Requested')}: {formatDate(request.createdAt, i18n.language)}</span>
      </div>

      <div className={styles.userMeta}>
        <span>{t('Department')}: {request.department || '-'}</span>
        <span>{t('Plant')}: {request.plant || '-'}</span>
        <span>{t('Team')}: {request.team || '-'}</span>
        <span>{t('Project')}: {request.project || '-'}</span>
      </div>

      <div className={styles.userMeta}>
        <span>{t('Reviewed by')}: {request.reviewedByUsername || '-'}</span>
        <span>{t('Reviewed')}: {formatDate(request.reviewedAt, i18n.language)}</span>
      </div>

      {request.note ? (
        <div className={styles.emptyState}>{request.note}</div>
      ) : null}

      {timelineItems.length ? (
        <div className={styles.timelinePanel}>
          <strong>{t('Timeline')}</strong>
          <div className={styles.timelineList}>
            {timelineItems.map((item) => (
              <div key={item.label} className={styles.timelineItem}>
                <span>{item.label}</span>
                <strong>{formatDate(item.value, i18n.language)}</strong>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {request.status === 'pending' ? (
        <>
          <label className={styles.toggleField}>
            <input
              type="checkbox"
              checked={draft.requireLicenseAgreement}
              onChange={(event) => updateRequestReviewDraft(request.id, 'requireLicenseAgreement', event.target.checked)}
              disabled={!approvalEmailConfigured}
            />
            {t('Require license agreement before activation')}
          </label>
          <Field label={t('Decline reason (optional)')}>
            <textarea
              value={draft.declineReason}
              onChange={(event) => updateRequestReviewDraft(request.id, 'declineReason', event.target.value)}
              rows={3}
            />
          </Field>
          <Field label={t('Internal admin note')}>
            <textarea
              value={draft.internalNote}
              onChange={(event) => updateRequestReviewDraft(request.id, 'internalNote', event.target.value)}
              rows={3}
            />
          </Field>
          <Field label={t('License package')}>
            <select
              value={draft.licensePackage || 'evaluation'}
              onChange={(event) => updateRequestReviewDraft(request.id, 'licensePackage', event.target.value)}
            >
              <option value="evaluation">{t('Evaluation')}</option>
              <option value="pilot">{t('Pilot')}</option>
              <option value="production">{t('Production Candidate')}</option>
            </select>
          </Field>
          <div className={styles.cardActions}>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={() => handleApproveAccessRequest(request)}
              disabled={approvingAccessRequestId === request.id || !approvalEmailConfigured}
            >
              {approvingAccessRequestId === request.id ? t('Approving...') : t('Approve and send email')}
            </button>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => handleDeclineAccessRequest(request)}
              disabled={decliningAccessRequestId === request.id}
            >
              {decliningAccessRequestId === request.id ? t('Declining...') : t('Decline request')}
            </button>
          </div>
        </>
      ) : (
        <div className={styles.userMeta}>
          <span>{t('Decline reason')}: {request.declineReason || '-'}</span>
          <span>{t('License required')}: {request.requireLicenseAgreement ? t('Yes') : t('No')}</span>
        </div>
      )}
    </article>
  );
}

function renderApprovalRequestCard({
  request,
  t,
  i18n,
  styles,
  resendingApprovalId,
  cancellingApprovalId,
  handleResendApproval,
  handleCancelApproval,
  setSelectedApprovalRequest,
}) {
  const timelineItems = buildTimelineItems([
    { label: t('Requested'), value: request.requestedAt },
    { label: t('Email sent'), value: request.emailSentAt },
    { label: t('Activated'), value: request.approvedAt },
    { label: t('Cancelled'), value: request.cancelledAt },
  ]);

  return (
    <article key={request.id} className={styles.userCard}>
      <div className={styles.userTopline}>
        <div>
          <span className={`${styles.roleBadge} ${approvalStatusClass(styles, request.status)}`}>
            {t(approvalStatusLabel(request.status))}
          </span>
          <h3>{request.username}</h3>
          <p>{request.email}</p>
        </div>
        <div className={styles.userId}>#{request.id}</div>
      </div>

      <div className={styles.userMeta}>
        <span>{t('Requested')}: {formatDate(request.requestedAt, i18n.language)}</span>
        <span>{t('Expires')}: {formatDate(request.expiresAt, i18n.language)}</span>
        <span>{t('Approved')}: {formatDate(request.approvedAt, i18n.language)}</span>
        <span>{t('License package')}: {request.licensePackage || '-'}</span>
      </div>

      <div className={styles.userMeta}>
        <span>{t('Requested by')}: {request.requestedByUsername || '-'}</span>
        <span>{t('Email sent')}: {formatDate(request.emailSentAt, i18n.language)}</span>
        <span>{t('License required')}: {request.requiresLicenseAgreement ? t('Yes') : t('No')}</span>
        <span>
          {t('Email delivery')}: <strong>{t(deliveryStatusLabel(request.deliveryStatus))}</strong>
          {request.deliveryProvider ? ` (${t(deliveryProviderLabel(request.deliveryProvider))})` : ''}
        </span>
      </div>

      {timelineItems.length ? (
        <div className={styles.timelinePanel}>
          <strong>{t('Timeline')}</strong>
          <div className={styles.timelineList}>
            {timelineItems.map((item) => (
              <div key={item.label} className={styles.timelineItem}>
                <span>{item.label}</span>
                <strong>{formatDate(item.value, i18n.language)}</strong>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className={styles.cardActions}>
        {request.deliveryError ? (
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => setSelectedApprovalRequest(request)}
          >
            {t('View exact error')}
          </button>
        ) : null}

        {request.status !== 'approved' ? (
          <>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => handleResendApproval(request)}
              disabled={resendingApprovalId === request.id || cancellingApprovalId === request.id}
            >
              {resendingApprovalId === request.id ? t('Sending...') : t('Send approval email')}
            </button>
            <button
              type="button"
              className={styles.dangerButton}
              onClick={() => handleCancelApproval(request)}
              disabled={cancellingApprovalId === request.id || resendingApprovalId === request.id}
            >
              {cancellingApprovalId === request.id ? t('Cancelling...') : t('Cancel invitation')}
            </button>
          </>
        ) : null}
      </div>
    </article>
  );
}

function buildTimelineItems(items) {
  return items.filter((item) => item?.value);
}
